import type { ScenarioCheck, ScenarioExpectations, ScenarioRules } from '@/lib/api/scenarios.api';

export const emptyRules = (): ScenarioRules => ({
  toolsMustCall: [],
  toolsMustNotCall: [],
  mustSay: [],
  mustNotSay: [],
  outcome: null,
  maxTurns: null,
});

export const emptyExpectations = (): ScenarioExpectations => ({
  checks: [],
  rules: emptyRules(),
});

/** Fills in any missing rule arrays so a partial draft from the API is safe to edit. */
export const normalizeExpectations = (
  input: Partial<ScenarioExpectations> | null | undefined,
): ScenarioExpectations => {
  const rules: Partial<ScenarioRules> = input?.rules ?? {};
  return {
    checks: (input?.checks ?? []).map((c, i) => ({
      id: c.id?.trim() || `c${i + 1}`,
      text: c.text ?? '',
    })),
    rules: {
      toolsMustCall: rules.toolsMustCall ?? [],
      toolsMustNotCall: rules.toolsMustNotCall ?? [],
      mustSay: rules.mustSay ?? [],
      mustNotSay: rules.mustNotSay ?? [],
      outcome: rules.outcome?.trim() ? rules.outcome.trim() : null,
      maxTurns: typeof rules.maxTurns === 'number' && rules.maxTurns > 0 ? rules.maxTurns : null,
    },
  };
};

export const hasAnyRule = (rules: ScenarioRules): boolean =>
  rules.toolsMustCall.length > 0 ||
  rules.toolsMustNotCall.length > 0 ||
  rules.mustSay.length > 0 ||
  rules.mustNotSay.length > 0 ||
  rules.outcome != null ||
  rules.maxTurns != null;

/**
 * Next `c<n>` id, one past the highest existing number. Deleted ids are never reused so
 * a check id keeps pointing at the same statement across edits and past judge outputs.
 */
export const nextCheckId = (checks: ScenarioCheck[]): string => {
  const highest = checks.reduce((max, c) => {
    const match = /^c(\d+)$/.exec(c.id);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
  return `c${highest + 1}`;
};

/** Returns an error message, or null when the expectations are valid. */
export const validateExpectations = (expectations: ScenarioExpectations): string | null => {
  const checks = expectations.checks.filter((c) => c.text.trim().length > 0);
  if (checks.length === 0 && !hasAnyRule(expectations.rules)) {
    return 'Add at least one check or rule';
  }
  if (expectations.checks.some((c) => c.text.trim().length === 0)) {
    return 'Every check needs a description (remove empty ones)';
  }
  const ids = expectations.checks.map((c) => c.id);
  if (new Set(ids).size !== ids.length) {
    return 'Check ids must be unique';
  }
  const { maxTurns } = expectations.rules;
  if (maxTurns != null && (!Number.isInteger(maxTurns) || maxTurns < 1 || maxTurns > 60)) {
    return 'Max turns must be a whole number between 1 and 60';
  }
  return null;
};

/** Trims text and drops empty entries so what we send matches the backend schema. */
export const serializeExpectations = (expectations: ScenarioExpectations): ScenarioExpectations => {
  const clean = (list: string[]) => list.map((s) => s.trim()).filter((s) => s.length > 0);
  return {
    checks: expectations.checks
      .map((c) => ({ id: c.id, text: c.text.trim() }))
      .filter((c) => c.text.length > 0),
    rules: {
      toolsMustCall: clean(expectations.rules.toolsMustCall),
      toolsMustNotCall: clean(expectations.rules.toolsMustNotCall),
      mustSay: clean(expectations.rules.mustSay),
      mustNotSay: clean(expectations.rules.mustNotSay),
      outcome: expectations.rules.outcome?.trim() ? expectations.rules.outcome.trim() : null,
      maxTurns: expectations.rules.maxTurns ?? null,
    },
  };
};
